/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, FilePlus2 } from 'lucide-react';
import { useState } from 'react';
import { Button, Empty, ErrorText, Modal, Spinner } from './ui';
import { api, getAccessToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** Кнопка «Документ по шаблону»: выбор шаблона для сущности, сохранение в документы карточки или скачивание. */
export function GenerateDoc({ entity, entityId, label = 'Документ по шаблону' }: { entity: string; entityId: string; label?: string }) {
  const { hasModule, can } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ enabled: open, queryKey: ['templates', entity], queryFn: () => api.get<any[]>(`/templates?entity=${entity}`) });
  const gen = useMutation({
    mutationFn: (t: any) => api.post(`/templates/${t.id}/generate`, { entityId, save: true }),
    onSuccess: (d: any) => { setDone(d.title); qc.invalidateQueries({ queryKey: ['documents'] }); },
  });
  const [dlErr, setDlErr] = useState<unknown>(null);
  if (!hasModule('M36') || !can('document.write')) return null;
  return (
    <>
      <Button variant="secondary" onClick={() => { setOpen(true); setDone(null); }}><FilePlus2 size={16} />{label}</Button>
      <Modal title="Документ по шаблону" open={open} onClose={() => setOpen(false)}>
        {isLoading ? <Spinner /> : !data?.length ? <Empty>Шаблонов для этой карточки нет. Добавьте их в разделе «Шаблоны документов».</Empty> : (
          <ul className="divide-y divide-slate-100">
            {data.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 py-2">
                <div className="min-w-0"><div className="text-sm">{t.name}</div>{t.category && <div className="text-xs text-slate-500">{t.category}</div>}</div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="ghost" title="Скачать, не сохраняя" onClick={async () => {
                    setDlErr(null);
                    try { await downloadGenerated(t.id, entityId); } catch (e) { setDlErr(e); }
                  }}><FileDown size={14} /></Button>
                  <Button size="sm" onClick={() => gen.mutate(t)} disabled={gen.isPending}>Сформировать</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <ErrorText error={gen.error ?? dlErr} />
        {done && <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">Документ «{done}» сохранён на вкладке «Документы».</div>}
      </Modal>
    </>
  );
}

async function downloadGenerated(templateId: string, entityId: string) {
  // POST со скачиванием: используем fetch через общий клиент
  const r = await fetch(`/api/v1/templates/${templateId}/generate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` }, body: JSON.stringify({ entityId, save: false }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message ?? 'Не удалось сформировать документ');
  const m = (r.headers.get('content-disposition') ?? '').match(/filename\*=UTF-8''([^;]+)/);
  const url = URL.createObjectURL(await r.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: m ? decodeURIComponent(m[1]) : 'document.docx' });
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}
